import type {
  CncSpeciesAssignment,
  ProposalNodeSet,
  RfqPackage,
  RfqProject,
  TimberRfqDrynessSpec,
  TimberSupplierStructureRef,
  VendorFileRelease,
  VendorWorkType,
} from '../types';
import type { TimberSpeciesDrynessEntry } from './supplierTimberExport';
import type { VendorPortalDealGroup } from './vendorPortalDealGroups';

export const VENDOR_PORTAL_PREVIEW_STORAGE_KEY = 'lctf_vendor_portal_preview';
export const PUBLISH_PREVIEW_DEAL_ID = '__publish_preview_deal__';
export const PUBLISH_PREVIEW_PKG_ID = '__publish_preview_pkg__';
export const PUBLISH_PREVIEW_RELEASE_ID = '__publish_preview_release__';

export type VendorPortalPreviewPayload = {
  orgId: string;
  orgName: string;
  workType: VendorWorkType;
  dealTitle: string;
  projectNumber?: string;
  projectName?: string;
  packageLabel: string;
  releaseVersionIndex?: number;
  requestedSpecies?: string[];
  timberDrynessSpec?: TimberRfqDrynessSpec | null;
  speciesDryness?: TimberSpeciesDrynessEntry[];
  /** CNC quote requests only — the species split shown on the preview package card. */
  cncSpeciesAssignments?: CncSpeciesAssignment[];
  /** CNC quote requests only — structures shown on the preview package card. */
  cncStructures?: TimberSupplierStructureRef[];
  shipToAddress?: import('../types').SupplierShipToAddress;
  nodeSets?: ProposalNodeSet[];
  rfqProjectId?: string;
  hubFileReleaseId?: string;
  sourceFileName?: string;
  releaseLabel?: string;
  releaseFileName?: string;
  releaseUrl?: string;
  linkedProposalVersionLabel?: string;
  linkedProposalVersionSavedAt?: string;
};

export function writeVendorPortalPreview(payload: VendorPortalPreviewPayload): void {
  try {
    sessionStorage.setItem(VENDOR_PORTAL_PREVIEW_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readVendorPortalPreview(): VendorPortalPreviewPayload | null {
  try {
    const raw = sessionStorage.getItem(VENDOR_PORTAL_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as VendorPortalPreviewPayload;
  } catch {
    return null;
  }
}

function previewDealTitle(payload: VendorPortalPreviewPayload): string {
  const num = (payload.projectNumber || '').trim();
  const numDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
  const name = (payload.projectName || payload.dealTitle || '').trim();
  if (numDisplay && name) return `${numDisplay} ${name}`;
  return name || payload.dealTitle;
}

/** Synthetic unpublished package + deal group for the publish-review iframe. */
export function buildPublishPreviewDealGroups(payload: VendorPortalPreviewPayload): VendorPortalDealGroup[] {
  const workType = payload.workType;
  const isCnc = workType === 'cnc_quote';
  const projectId = payload.rfqProjectId?.trim() || 'publish-preview-project';
  const now = new Date();
  const speciesDrynessMap = Object.fromEntries(
    (payload.speciesDryness ?? []).map(entry => [entry.species, entry.drynessSpec]),
  );

  const project: RfqProject = {
    id: projectId,
    supplierOrgId: payload.orgId,
    dealId: PUBLISH_PREVIEW_DEAL_ID,
    dealTitle: payload.dealTitle,
    status: 'active',
    workType,
    timberDrynessSpec: payload.timberDrynessSpec ?? undefined,
    timberSupplierInvites: payload.requestedSpecies?.length
      ? [
          {
            supplierOrgId: payload.orgId,
            requestedSpecies: payload.requestedSpecies,
            speciesDryness: Object.keys(speciesDrynessMap).length ? speciesDrynessMap : undefined,
          },
        ]
      : undefined,
    createdAt: now,
  };

  const pkg: RfqPackage = {
    id: PUBLISH_PREVIEW_PKG_ID,
    rfqProjectId: projectId,
    supplierOrgId: payload.orgId,
    dealId: PUBLISH_PREVIEW_DEAL_ID,
    dealTitle: payload.dealTitle,
    label: payload.packageLabel,
    releaseDisplayLabel: payload.releaseLabel?.trim() || undefined,
    linkedProposalVersionLabel: payload.linkedProposalVersionLabel?.trim() || undefined,
    linkedProposalVersionSavedAt: payload.linkedProposalVersionSavedAt
      ? new Date(payload.linkedProposalVersionSavedAt)
      : undefined,
    workType,
    releaseVersionIndex: payload.releaseVersionIndex ?? 1,
    hubFileReleaseId: payload.hubFileReleaseId,
    proposalSnapshot: payload.nodeSets?.length ? { nodeSets: payload.nodeSets } : undefined,
    shipToAddress: payload.shipToAddress,
    cncPackagePayload:
      isCnc && (payload.releaseFileName || payload.releaseUrl)
        ? {
            boardFeet: (payload.cncSpeciesAssignments ?? []).reduce(
              (sum, a) => sum + (a.boardFeet || 0),
              0,
            ),
            releasedFileIds: [PUBLISH_PREVIEW_RELEASE_ID],
            ...(payload.cncSpeciesAssignments?.length
              ? { speciesAssignments: payload.cncSpeciesAssignments }
              : {}),
            ...(payload.cncStructures?.length ? { structures: payload.cncStructures } : {}),
          }
        : undefined,
    createdAt: now,
  };

  return [
    {
      dealId: PUBLISH_PREVIEW_DEAL_ID,
      title: previewDealTitle(payload),
      timber: isCnc ? null : { project, packages: [pkg] },
      cnc: isCnc ? { project, packages: [pkg] } : null,
    },
  ];
}

export function buildPublishPreviewVendorReleases(
  payload: VendorPortalPreviewPayload,
): VendorFileRelease[] {
  if (payload.workType !== 'cnc_quote') return [];
  if (!payload.releaseFileName && !payload.releaseUrl) return [];

  const projectId = payload.rfqProjectId?.trim() || 'publish-preview-project';
  return [
    {
      id: PUBLISH_PREVIEW_RELEASE_ID,
      rfqProjectId: projectId,
      supplierOrgId: payload.orgId,
      dealId: PUBLISH_PREVIEW_DEAL_ID,
      sourceFileId: '',
      sourceStreamId: '',
      sourceStreamTitle: '',
      sourceProjectId: '',
      displayLabel: payload.releaseLabel || payload.releaseFileName || 'CNC Files',
      releaseVersionIndex: payload.releaseVersionIndex ?? 1,
      storagePath: '',
      releasedUrl: payload.releaseUrl || '',
      fileName: payload.releaseFileName || payload.releaseLabel || 'cadwork.3d',
      releasedByUid: '',
      createdAt: new Date(),
    },
  ];
}
